import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "./ui/dropdown-menu";
import {
  Menu,
  Home,
  Settings,
  User,
  LogOut,
  Clock,
  Check,
  Palette,
  Plug,
  Blocks,
  Dumbbell,
  Plus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { signOut } from "supertokens-auth-react/recipe/session";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/lib/store";
import { resetBusinessAndLocation } from "@/store/slices/locationIdSlice";
import { Button } from "./ui/button";
import { useTheme } from "@/hooks/useTheme";
import { useLocationsViewsGetIntegrationsForLocationQuery } from "@/store/airloom-generated-rtk";
import { getIntegrationProvider } from "@/lib/integrationRegistry";
import React from "react";

interface NavMenuProps {
  user: any;
}

export function NavMenu({ user }: NavMenuProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme, setTheme } = useTheme();
  const selectedBusinessId = useSelector(
    (state: RootState) => state.businessAndLocation.selectedBusinessId,
  );
  const selectedLocationId = useSelector(
    (state: RootState) => state.businessAndLocation.selectedLocationId,
  );
  const isMachineUsageEnabled = useSelector(
    (state: RootState) => state.machineUsage.isEnabled,
  );

  // Fetch integrations for the selected location
  const { data: integrationsData } =
    useLocationsViewsGetIntegrationsForLocationQuery(
      { locationId: selectedLocationId! },
      { skip: !selectedLocationId },
    );

  const handleLogout = async () => {
    dispatch(resetBusinessAndLocation());
    sessionStorage.clear();
    await signOut();
    navigate("/");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-9 w-9 px-0">
          <Menu className="w-5 h-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{user?.first_name || "User"}</p>
          <p className="text-xs text-muted-foreground break-all">
            {user?.email}
          </p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/search")}>
          <Home className="mr-2 h-4 w-4" />
          Home
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/search/recurring")}>
          <Clock className="mr-2 h-4 w-4" />
          Recurring Searches
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/integrations")}>
          <Plug className="mr-2 h-4 w-4" />
          Integrations
        </DropdownMenuItem>
        {integrationsData?.success &&
          integrationsData.integrations.length > 0 &&
          integrationsData.integrations.map((integration) => {
            const provider = getIntegrationProvider(
              integration.type.toLowerCase(),
            );
            if (!provider) return null;

            return (
              <DropdownMenuItem
                key={integration.id}
                onClick={() =>
                  navigate(
                    `/location/${selectedLocationId}/integrations/${provider.id}`,
                  )
                }
                inset // Built-in prop for indentation
              >
                {provider.iconType === "lucide" ? (
                  React.createElement(
                    provider.icon as React.ComponentType<{
                      className?: string;
                    }>,
                  )
                ) : (
                  <img
                    src={provider.icon as string}
                    alt={`${provider.name} icon`}
                    className="h-4 w-4 object-contain"
                  />
                )}
                {provider.navText}
              </DropdownMenuItem>
            );
          })}
        <DropdownMenuItem onClick={() => navigate("/modules")}>
          <Blocks className="mr-2 h-4 w-4" />
          Modules
        </DropdownMenuItem>
        {isMachineUsageEnabled && (
          <DropdownMenuItem
            onClick={() => navigate("/modules/machine-usage")}
            inset
          >
            <Dumbbell className="mr-2 h-4 w-4" />
            Machine Usage
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate("/configure")}>
          <Settings className="mr-2 h-4 w-4" />
          Configuration
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/user")}>
          <User className="mr-2 h-4 w-4" />
          Account
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Palette className="mr-2 h-4 w-4" />
            <span>Theme</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              <DropdownMenuItem
                onClick={() => setTheme("light")}
                className="pr-8"
              >
                <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                  {theme === "light" && <Check className="h-4 w-4" />}
                </span>
                Light
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setTheme("dark")}
                className="pr-8"
              >
                <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                  {theme === "dark" && <Check className="h-4 w-4" />}
                </span>
                Dark
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setTheme("system")}
                className="pr-8"
              >
                <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                  {theme === "system" && <Check className="h-4 w-4" />}
                </span>
                System
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
